// Catalog.ID Client — `issue-cert`: the chat_sign sub-key certification ceremony
// ============================================================================
// Custody surface for the two-level `chat_sign` model (chat migration §3;
// normative format: apps/chat/docs/impl/chat-sign-certs.md). The account-level
// ROOT lives in the CID keybag and NEVER leaves this tab; a per-machine chat
// SUB-KEY is presented here by the chat module and goes back certified.
//
// Custody boundary = the CID tab, decided 2026-08-26. A dedicated key-agent
// process stays a later option; OS keystore/TPM for sub-keys is deferred.
//
// WHY THERE IS NO CONSENT MODAL, and what stands in its place
// -----------------------------------------------------------
// The gate here is the keybag UNLOCK, not a per-request prompt. Three reasons:
//   1. §3's custody doctrine makes unlock itself the ceremony — "ceremony-grade
//      operations REQUIRE an explicit unlock by design".
//   2. §3 states renewal "piggybacks on any keybag unlock". A modal per
//      issuance would fire on every device every 60 days, which trains users
//      to click through exactly the prompt that matters.
//   3. Nothing here MUTATES the keybag, so the per-write supersecret
//      re-prompt (audit M12) does not apply:
//      this reads one entry and signs with it, the same shape as the EFS
//      `decapsulate-efs-encrypt` hop, which is likewise unlock-gated only.
// What DOES gate the request: the hub's `_senderServiceId` stamp, the relay's
// module-identity signature check, and the digest binding below.
//
// Gate stack (in order):
//   1–3. sender stamp / requester / active identity — `dispatchChatRequest`.
//   4.   required fields present.
//   5.   `subkeyPubkey` is decodable base64.
//   6.   body↔canonical binding: SHA-512(raw sub-key) equals the signed
//        `subkeyPubkeyDigestBase64`. Closes the substituted-body gap exactly
//        as `get-claims` does with its cids digest.
//   7.   sub-key is EXACTLY 1984 bytes — §17 "sub-keys are hybrid, always".
//   8.   `validityDays` is a whole 1…90; default 60.
//   9.   wait-for-unlock (90 s when `requiresUserInteraction:true`, else fail
//        fast — nobody would see a prompt for a request that does not refocus
//        the tab).
//  10.   the `chat_sign` root is in the bag and is a 64-byte hybrid seed.
//  11.   issue via crypto/chat-certs (which re-checks 7, the 1…90 range, and
//        refuses root-certifies-itself), zeroizing the seed copy afterwards.
// ============================================================================

import { sha512 } from '@noble/hashes/sha2.js';
import { keys } from '../state/keys';
import { recordKeyUse } from '../state/key-use-record';
import * as aes from '../crypto/aes';
import {
    issueCert, CHAT_CERT_DEFAULT_VALIDITY_DAYS, CHAT_CERT_MAX_VALIDITY_DAYS,
} from '../crypto/chat-certs';
import { HYBRID_PUBLIC_KEY_LEN, HYBRID_SECRET_SEED_LEN } from '../crypto/signing';
import type { ChatRequest, ChatResponse, ChatDispatchDeps } from './chat-types';
import { ensureUnlocked } from './unlock-gate';

/**
 * Keybag slot for the account-level chat signing root (migration §3). Bag-only
 * — the private half never gets an `accountpublickey` row, so the key id must
 * be on `KeyBagEntryRepository`'s consistency skip-list or every write throws
 * (§17). The PUBLIC half publishes on the `registry_signing_key` claim track
 * under the `chat_root` class — the once-per-account ceremony in
 * `pane-types/chat-root-pane.ts` on top of `crypto/chat-root-publication.ts`
 * (chat migration §14 M3). Until that has run for an account, a cert issued
 * here chains to nothing: the verification chain is message sig → cert → the
 * root's window on the claim track, and an unpublished root has no window.
 * `dev/chat-cert.ts publish-root` remains the headless equivalent.
 */
export const CHAT_SIGN_KEY_ID = 'chat_sign';

type ResponseBase = Pick<ChatResponse, 'type' | 'to' | 'requestId'>;

/**
 * Digest convention shared with the chat service's canonical builder and the
 * relay's C# mirror (`CrossModuleCanonical.ComputeSubkeyPubkeyDigest`): base64
 * SHA-512 over the RAW sub-key public key bytes, NOT over its base64 text. Hashing the
 * raw bytes keeps the commitment independent of base64 padding/encoding
 * choices on either side.
 */
export function computeSubkeyPubkeyDigest(subkeyPubkey: Uint8Array): string {
    return aes.toBase64(sha512(subkeyPubkey));
}

function unavailable(base: ResponseBase, reason: string): ChatResponse {
    return { ...base, status: 'unavailable', reason };
}

export async function handleIssueCert(
    request: ChatRequest,
    base: ResponseBase,
    accountCid: string,
    deps: ChatDispatchDeps,
): Promise<ChatResponse> {
    // Gate 4.
    if (!request.subkeyPubkey || !request.subkeyPubkeyDigestBase64) {
        return unavailable(base, 'missing-required-field');
    }

    // Gate 5.
    let subkeyPubkey: Uint8Array;
    try {
        subkeyPubkey = aes.fromBase64(request.subkeyPubkey);
    } catch {
        return unavailable(base, 'bad-subkey-base64');
    }

    // Gate 6: body↔canonical binding.
    if (computeSubkeyPubkeyDigest(subkeyPubkey) !== request.subkeyPubkeyDigestBase64) {
        return unavailable(base, 'subkey-digest-mismatch');
    }

    // Gate 7: §17 — an Ed25519-only sub-key is refused as a FORMAT error, here
    // and again inside issueCert. Cheap, and it means a broken requester is
    // told at issuance rather than shipping a cert no verifier will honour.
    if (subkeyPubkey.length !== HYBRID_PUBLIC_KEY_LEN) {
        return unavailable(base, 'subkey-not-hybrid');
    }

    // Gate 8.
    const validityDays = request.validityDays ?? CHAT_CERT_DEFAULT_VALIDITY_DAYS;
    if (!Number.isInteger(validityDays)
        || validityDays < 1 || validityDays > CHAT_CERT_MAX_VALIDITY_DAYS) {
        return unavailable(base, 'validity-out-of-range');
    }

    // Gate 9: wait-for-unlock. `requiresUserInteraction:true` is what makes the
    // dashboard refocus this tab, so it is also what makes waiting meaningful.
    if (!await ensureUnlocked(request, deps)) return unavailable(base, 'cid-locked');

    // Gate 10, and ABSENT IS NOT LOCKED (device-setup-restructure §1 item 12,
    // §9.2). The chat root is an OPTIONAL key: a member may keep it off this
    // device (whitepaper §3.4.7). Certification is the interactive, tab-
    // refocusing request, so it is exactly the one that may load it back —
    // `requireKey` prompts for the supersecret, decrypts this one key from the
    // server bag and offers to keep it here. Everything else answers
    // `key-not-on-device`, which is neither `cid-locked` nor "your root is
    // gone".
    let rootSeedB64 = keys.getPrivateKey(CHAT_SIGN_KEY_ID);
    if (!rootSeedB64) {
        if (request.requiresUserInteraction === true && deps.requireKey) {
            try {
                await deps.requireKey(CHAT_SIGN_KEY_ID, 'certify this machine for chat');
            } catch {
                // A failed load is an absent key; the refusal below says so.
            }
            rootSeedB64 = keys.getPrivateKey(CHAT_SIGN_KEY_ID);
        }
        if (!rootSeedB64) return unavailable(base, 'key-not-on-device');
    }
    let rootSeed: Uint8Array;
    try {
        rootSeed = aes.fromBase64(rootSeedB64);
    } catch {
        return unavailable(base, 'chat-sign-root-malformed');
    }
    if (rootSeed.length !== HYBRID_SECRET_SEED_LEN) {
        rootSeed.fill(0);
        return unavailable(base, 'chat-sign-root-malformed');
    }

    // Gate 11. The seed copy is zeroed on every path — the keybag is a root of
    // certification, never an operational key (§3 custody doctrine). Zeroing
    // this copy does not touch the bag's own base64 string; the entry stays
    // usable for the next renewal.
    try {
        const cert = await issueCert(rootSeed, subkeyPubkey, {
            // Bound to the ACTIVE identity. The requester does not get to name
            // the account it is being certified under.
            accountCid,
            validityDays,
        });
        // Evidence of use (§9.4): written only now that the cert exists, and as
        // the ORACLE it was — the root itself never left this tab.
        recordKeyUse(accountCid, CHAT_SIGN_KEY_ID, {
            requesterId: request.requesterId,
            mode: request.mode,
            kind: 'oracle',
        });
        return {
            ...base,
            status: 'approved',
            cert,
            subkeyFingerprint: toHexLower(sha512(subkeyPubkey)),
            rootFingerprint: cert.body.rootFingerprint,
        };
    } catch (err) {
        // issueCert's own refusals are stable codes (`subkey-is-root`,
        // `subkey-not-hybrid`, `validity-out-of-range`, `bad-account-cid`,
        // `bad-root-seed`); pass them through verbatim so chat can branch.
        const message = err instanceof Error ? err.message.slice(0, 80) : 'cert-issue-failed';
        return unavailable(base, message);
    } finally {
        rootSeed.fill(0);
    }
}

function toHexLower(bytes: Uint8Array): string {
    let out = '';
    for (let i = 0; i < bytes.length; i++) {
        const b = bytes[i] as number;
        out += (b < 16 ? '0' : '') + b.toString(16);
    }
    return out;
}
